// LinkedAuditEventPanel
//
// For Microsoft-security PASS-THROUGH alerts (rule_defender_endpoint_alert_passthrough
// — e.g. "Mailbox Permission Change") the Defender payload names only the actor. What
// actually changed — the target mailbox, the trustee, the access rights — lives only
// in the Exchange admin audit event we ingest. This panel renders that resolved detail
// and, on real alerts, exposes a "Link source audit event" button that runs the
// on-demand correlation (LinkSourceAuditEvent) and persists the result.
// (handoff 2026-07-11_Alert-to-Audit-Event-Linking-Portal-Handoff)
//
// Rendered by both AlertsPage and TestRunResultsPage (CLAUDE.md rule #13 parity).
// The button is Alerts-only (showLinkButton) — test rows carry only the inline
// pipeline stamp, never a portal write-back.
//
// 0 matches  -> "not yet ingested, retry later" (existing link untouched).
// >1 matches -> candidate picker; the investigator's pick is written back.
// 1 match    -> resolve + write back + refresh.

import React, { useCallback, useMemo, useState } from 'react';
import { ArrowPathIcon, LinkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { linkSourceAuditEvent } from '../../api/client';
import { normalizeIsoUtc, resolveTz } from '../../utils/timeFormat';
import { extractLinkInputs, effectiveLinkedAuditEvent, linkedEventList } from '../../utils/linkedAuditEvent';

function fmtTs(ts, tzIana) {
  if (!ts) return null;
  try {
    // resolveTz maps "Local" (empty iana) -> browser zone (empty timeZone throws);
    // normalizeIsoUtc fixes zone-less / sub-ms parsing. See feedback-local-tz-empty-iana.
    const zone = resolveTz(tzIana);
    const d = new Date(normalizeIsoUtc(ts));
    if (zone === 'UTC') {
      return d.toISOString().replace('T', ' ').replace(/\.\d{3}Z$/, 'Z');
    }
    return d.toLocaleString('en-US', {
      timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch { return String(ts); }
}

function DetailRow({ label, children }) {
  return (
    <div className="flex gap-2 items-baseline">
      <span className="text-[10px] uppercase tracking-wide text-gray-500 w-32 flex-none">{label}</span>
      <span className="text-gray-200 break-words min-w-0">{children}</span>
    </div>
  );
}

export default function LinkedAuditEventPanel({ alert, rawEvent, tzIana, showLinkButton = false, boxed = false, onResolved }) {
  const [resolved, setResolved] = useState(null);      // link written this session
  const [candidates, setCandidates] = useState(null);  // ambiguous result
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);                // info / retry text

  const inputs = useMemo(() => extractLinkInputs(rawEvent), [rawEvent]);
  const baseLink = useMemo(() => effectiveLinkedAuditEvent(alert, rawEvent), [alert, rawEvent]);
  const effective = resolved || baseLink;
  const events = useMemo(() => linkedEventList(effective), [effective]);
  const isMulti = events.length > 1;

  const doLink = useCallback(async ({ chosenEventId, linkAll } = {}) => {
    if (loading || !inputs.canLink) return;
    setLoading(true); setErr(null); setMsg(null);
    try {
      const res = await linkSourceAuditEvent(alert.alertId, {
        tenantId: alert.tenantId,
        actorObjectId: inputs.actorObjectId,
        actorUpn: inputs.actorUpn || alert.userId,
        alertTitle: inputs.alertTitle,
        winStart: inputs.winStart,
        winEnd: inputs.winEnd,
        chosenEventId,
        linkAll,
      });
      if (res?.found && res.linkedAuditEvent) {
        setResolved({ ...res.linkedAuditEvent, _source: 'portal' });
        setCandidates(null);
        // Lift the result to the parent so it lands in the in-memory alerts array —
        // otherwise collapsing + re-expanding the row (which unmounts this panel)
        // reverts to the pre-link display. No-op where onResolved is absent (test runs).
        onResolved?.(alert.alertId, res);
      } else if (res?.reason === 'ambiguous') {
        setCandidates(Array.isArray(res.candidates) ? res.candidates : []);
        setMsg('Multiple candidate events matched — pick the correct one.');
      } else if (res?.reason === 'title_not_supported') {
        setMsg('Linking is not available for this alert type.');
      } else {
        // not_yet_ingested — leave any existing link untouched.
        setMsg('Source audit event not yet ingested (Microsoft delivery can lag ~1h). Try again later.');
      }
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [alert?.alertId, alert?.tenantId, alert?.userId, inputs, loading, onResolved]);

  // Nothing resolved and nothing linkable -> render nothing.
  if (!effective && !inputs.canLink) return null;

  const container = boxed ? 'rounded border p-3 text-xs' : 'px-6 py-3 border-b text-xs';
  const toneBorderBg = effective ? 'border-sky-900/40 bg-sky-950/10' : 'border-gray-800/60';
  // Mailbox(es) touched across all linked events — usually one shared mailbox.
  const distinctMailboxes = [...new Set(events.map((e) => e.changed_mailbox_upn).filter(Boolean))];
  const sharedMailbox = distinctMailboxes.length === 1 ? distinctMailboxes[0] : null;
  const anyMailboxDisabled = events.some((e) => e.changed_mailbox_account_enabled === false);
  const linkedBy = effective?.linked_by || (effective?._source === 'inline' ? 'pipeline' : 'analyst');
  const linkedUtc = alert?.linkedAuditCheckedUtc || effective?.linked_utc;

  return (
    <div className={`${container} ${toneBorderBg}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <LinkIcon className="w-4 h-4 flex-none mt-0.5 text-sky-400" />
          <div className="min-w-0">
            <p className="text-sky-200 font-semibold">Source audit event</p>
            {!effective && (
              <p className="text-gray-400 text-[11px] mt-0.5">
                This pass-through alert names only the actor. Link the Exchange admin audit
                event to reveal the mailbox that changed, the trustee, and the rights granted.
              </p>
            )}
          </div>
        </div>

        {/* Link / Re-check button (Alerts page only) */}
        {showLinkButton && inputs.canLink && (
          <div className="flex-none text-right">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); doLink({ linkAll: true }); }}
              disabled={loading}
              title={effective ? 'Re-run the correlation and overwrite the current link(s).' : 'Correlate this alert to every matching source audit event and save them.'}
              className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-sky-500/40 bg-sky-950/30 text-sky-300 hover:bg-sky-900/40 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading ? <ArrowPathIcon className="w-3 h-3 animate-spin" /> : <LinkIcon className="w-3 h-3" />}
              {loading ? 'Linking…' : (effective ? 'Re-check source events' : 'Link source audit event')}
            </button>
          </div>
        )}
      </div>

      {/* Resolved detail — one event renders inline; multiple render as a list. */}
      {effective && events.length === 1 && (
        <div className="mt-2 space-y-1">
          <DetailRow label="Mailbox changed">
            <span className="text-amber-300">{events[0].changed_mailbox_upn || '—'}</span>
            {anyMailboxDisabled && (
              <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-red-950/40 border border-red-500/40 text-red-300">
                <ExclamationTriangleIcon className="w-3 h-3" /> account disabled
              </span>
            )}
          </DetailRow>
          <DetailRow label="Access granted to">
            <span className="text-sky-300">{events[0].trustee_upn || '—'}</span>
          </DetailRow>
          <DetailRow label="Permission">
            {events[0].access_rights || '—'}
            {events[0].inheritance_type ? ` · inheritance ${events[0].inheritance_type}` : ''}
          </DetailRow>
          <DetailRow label="Source operation">
            {events[0].linked_operation || '—'}
            {events[0].source_client_ip ? ` · ${events[0].source_client_ip}` : ''}
            {events[0].source_creation_time ? ` · ${fmtTs(events[0].source_creation_time, tzIana)}` : ''}
          </DetailRow>
          <p className="text-[10px] text-gray-600 pt-1">
            Linked by {linkedBy}
            {linkedUtc ? ` · ${fmtTs(linkedUtc, tzIana)}` : ''}
            {' · event '}{events[0].linked_source_event_id}
          </p>
        </div>
      )}

      {effective && isMulti && (
        <div className="mt-2 space-y-2">
          <p className="text-[11px] text-gray-300">
            <span className="text-sky-200 font-semibold">{events.length}</span> permission changes linked
            {sharedMailbox && (
              <> to <span className="text-amber-300">{sharedMailbox}</span></>
            )}
            {sharedMailbox && anyMailboxDisabled && (
              <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-red-950/40 border border-red-500/40 text-red-300">
                <ExclamationTriangleIcon className="w-3 h-3" /> account disabled
              </span>
            )}
          </p>
          <div className="space-y-1">
            {events.map((ev, i) => (
              <div
                key={ev.linked_source_event_id || i}
                className="px-2 py-1.5 rounded border border-sky-900/40 bg-sky-950/20"
              >
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] items-baseline">
                  {!sharedMailbox && (
                    <span className="text-amber-300">{ev.changed_mailbox_upn || '—'}</span>
                  )}
                  <span className="text-gray-500">Access granted to</span>
                  <span className="text-sky-300">{ev.trustee_upn || '—'}</span>
                  {ev.access_rights && <span className="text-gray-300">{ev.access_rights}</span>}
                  {ev.inheritance_type && <span className="text-gray-500">inheritance {ev.inheritance_type}</span>}
                  {ev.source_creation_time && <span className="text-gray-500">{fmtTs(ev.source_creation_time, tzIana)}</span>}
                  {ev.source_client_ip && <span className="text-gray-600">{ev.source_client_ip}</span>}
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-gray-600">
            Linked by {linkedBy}{linkedUtc ? ` · ${fmtTs(linkedUtc, tzIana)}` : ''}
          </p>
        </div>
      )}

      {/* Candidate picker (fallback — the main button links all matches; this only
          appears if a call returned 'ambiguous', e.g. linkAll wasn't honored). */}
      {candidates && candidates.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <p className="text-[10px] uppercase tracking-wide text-gray-500">Candidate events</p>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); doLink({ linkAll: true }); }}
              disabled={loading}
              className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-sky-500/40 bg-sky-950/30 text-sky-300 hover:bg-sky-900/40 disabled:opacity-40"
            >
              <LinkIcon className="w-3 h-3" /> Link all {candidates.length} events
            </button>
          </div>
          <p className="text-[11px] text-gray-400 mb-1.5">
            Link all of these, or click a single event to link just that one — either way it saves to the alert (survives refresh).
          </p>
          <div className="space-y-1">
            {candidates.map((c) => (
              <button
                key={c.eventId}
                type="button"
                onClick={(e) => { e.stopPropagation(); doLink({ chosenEventId: c.eventId }); }}
                disabled={loading}
                className="w-full text-left px-2 py-1.5 rounded border border-gray-700 bg-gray-900/40 hover:bg-gray-800/60 disabled:opacity-40"
              >
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px]">
                  <span className="text-gray-400">{fmtTs(c.creationTime, tzIana)}</span>
                  <span className="text-gray-200">{c.operation}</span>
                  <span className="text-amber-300">{c.targetRaw || '—'}</span>
                  <span className="text-sky-300">→ {c.trusteeRaw || '—'}</span>
                  {c.accessRights && <span className="text-gray-400">{c.accessRights}</span>}
                  {c.clientIp && <span className="text-gray-500">{c.clientIp}</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Feedback */}
      {err && (
        <p className="text-[11px] mt-2 px-2 py-1 rounded bg-red-950/40 border border-red-500/30 text-red-300">
          Link failed: {err}
        </p>
      )}
      {msg && !err && (
        <p className="text-[11px] mt-2 text-amber-300">{msg}</p>
      )}
    </div>
  );
}
